import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { z } from 'zod';
import { permissions } from 'shared/constants/permissions';
import { useCopilotFrontendTool, zodToolSchema } from '../hooks/useCopilotFrontendTool';
import { COPILOT_NAVIGATION_TARGETS, findCopilotNavigationTarget } from './navigationCatalog';
interface GetNavigationTargetDetailsParams {
  targetId: string;
}
interface GetNavigationTargetDetailsResult {
  success: boolean;
  message: string;
  target?: {
    id: string;
    label: string;
    path: string;
    description: string;
    extendedDescription: string;
  };
  availableTargets?: Array<{
    id: string;
    label: string;
    description: string;
  }>;
}

/**
 * Registers the silent `get_navigation_target_details` frontend tool.
 *
 * Returns multi-line `extendedDescription` for an allowlisted route id without
 * putting that detail in default agent context. Presentation is `hidden`.
 */
export const useGetNavigationTargetDetailsTool = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = COPILOT_NAVIGATION_TARGETS.map(_temp).join(", ");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const availableTargetsDescription = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "get_navigation_target_details",
      description: `Look up detailed product documentation for an Alvie navigation target before proposing change_url. Available target ids: ${availableTargetsDescription}. Invoke only via call_frontend_tool. Use when the short catalog description is not enough to decide whether a page fits the user's goal. Does not navigate.`,
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      parameters: zodToolSchema(z.object({
        targetId: z.string().describe("The route id to describe. Must be one of the available navigation target ids.")
      })),
      handler: _temp3
    };
    t2 = [availableTargetsDescription];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useCopilotFrontendTool(t1, t2);
};
function _temp(target) {
  return `${target.id}: ${target.label}`;
}
function _temp2(t0) {
  const {
    id,
    label,
    description
  } = t0;
  return {
    id,
    label,
    description
  };
}
function _temp3(params) {
  const target_0 = findCopilotNavigationTarget(params.targetId);
  if (target_0 == null) {
    return {
      success: false,
      message: `Unknown navigation target "${params.targetId}".`,
      availableTargets: COPILOT_NAVIGATION_TARGETS.map(_temp2)
    };
  }
  return {
    success: true,
    message: `Loaded details for ${target_0.label}.`,
    target: {
      id: target_0.id,
      label: target_0.label,
      path: target_0.path,
      description: target_0.description,
      extendedDescription: target_0.extendedDescription
    }
  };
}